import { useMemo } from 'react';
import { clsx } from 'clsx';
import { toast, useNavigate, useQueryState, useMutation, useI18n } from '@ywwwtseng/react-kit';
import BigNumber from 'bignumber.js';

import { Typography } from '@ywwwtseng/tma-react-kit';
import * as web3 from '@ywwwtseng/web3';
import { useWalletKitConnect } from '@ywwwtseng/wallet-kit';
import { type FormElement, Form, validator } from '@/lib/react-form';
import { TokenIcon } from '@/components/common/TokenIcon';
import { EmailOtpButton } from '@/components/common/EmailOtpButton';
import { useTokens } from '@/hooks/useTokens';
import { useToken } from '@/hooks/useToken';
import { useConnectedGuard } from '@/hooks/useConnectedGuard';
import { useUserBalance } from '@/hooks/useUserBalance';
import { useWithdrawForm } from '@/hooks/useWithdrawForm';
import { useTokenAmountPrice } from '@/hooks/useTokenAmountPrice';
import { useWallets } from '@/hooks/useWallets';
import { useThrottle } from '@/hooks/useThrottle';
import * as utils from '@/utils';

export default function WithdrawConfirmScreen({
  params,
}: {
  params: { token_id?: string };
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const wallets = useWallets();
  const { signTransaction } = useWalletKitConnect();
  const { mutate, isLoading } = useMutation('tx:withdraw');
  const networks = useQueryState<string[]>('networks');
  const tokens = useTokens();
  const form = useWithdrawForm(params.token_id);
  const network = form.values.network as string;
  const amount = form.values.amount as string;
  const token_id = form.values.token_id as string;
  const token = useToken(token_id);
  const nativeToken = useToken(utils.token.getNativeTokenId(network));
  const balance = useUserBalance(token);
  const nativeBalance = useUserBalance(nativeToken);
  const { decimals } = utils.token.parseTokenId(token_id);
  const recommendedNetworkFee = utils.app.getRecommendedNetworkFee({
    network,
  });

  const insufficientNetworkFeeMessage = useMemo(() => {
    if (!recommendedNetworkFee || !token || !nativeToken) return '';

    if (
      !utils.app.isInsufficientNetworkFee({
        token,
        amount,
        balance: nativeBalance.value,
      })
    ) {
      return '';
    }

    return t('common.recommended_network_fee', {
      fee: web3.utils.formatUnits(recommendedNetworkFee, nativeToken.decimals),
      symbol: nativeToken.symbol,
    });
  }, [nativeBalance.value, recommendedNetworkFee, token, nativeToken]);

  // const { data: withdraw_fee } = useQuery<string>('withdraw_fee', {
  //   params: {
  //     network,
  //     token_id,
  //     amount: web3.utils.parseUnits(amount, decimals).toString(),
  //   },
  //   refetchOnMount: true,
  // });

  const withdraw_fee = utils.app.getRecommendedNetworkFee({
    network,
  })

  const insufficient = useMemo(() => {
    if (!withdraw_fee) {
      return false;
    }

    return BigNumber(nativeBalance.value).lt(BigNumber(withdraw_fee));
  }, [balance.value, amount, decimals]);

  const { formatted } = useTokenAmountPrice(
    token_id,
    web3.utils.parseUnits(amount, decimals).toString()
  );

  const withdrawCallback = useConnectedGuard(
    async () => {
      try {
        if (!token || insufficient) {
          return;
        }

        const destination = await wallets.sync(network);

        if (!destination) {
          toast.error(t('message.destination_not_set'));
          return;
        }

        if (network === 'solana') {
          if (!wallets.offical.solana) {
            toast.error(t('message.official_wallet_not_set'));
            return;
          }

          // const transaction = await signTransaction({
          //   feePayer: destination,
          //   source: wallets.offical.solana.address,
          //   destination,
          //   token,
          //   amount: web3.utils.parseUnits(amount, decimals),
          // });

          // const serialized = transaction
          //   .serialize({ requireAllSignatures: false })
          //   .toString('base64');

          void mutate({
            otp: form.values.otp,
            // transaction: serialized,
            network,
            token_address: token.token_address,
            amount: web3.utils.parseUnits(amount, decimals).toString(),
          }).then((result) => {
            if (result.ok) {
              navigate(-1);
              form.reset();
            }
          });
        } else {
          void mutate({
            otp: form.values.otp,
            network,
            token_address: token.token_address,
            amount: web3.utils.parseUnits(amount, decimals).toString(),
          }).then((result) => {
            if (result.ok) {
              navigate(-1);
              form.reset();
            }
          });
        }
      } catch (error) {
        console.error(error);
      }
    },
    { verified: true, network: token?.network }
  );

  const [withdraw, isThrottled] = useThrottle(
    withdrawCallback,
    5000,
    (remainingSeconds) => {
      toast.error(t('message.too_frequent', { time: String(remainingSeconds) }));
    }
  );

  const elements = useMemo<{ row: FormElement[] }[]>(() => {
    return [
      {
        row: [
          {
            ui: 'text',
            name: 'otp',
            validate: [
              validator.required({
                message: t('validation.required', {
                  name: t('common.otp'),
                }),
              }),
            ],
            props: {
              placeholder: t('common.otp'),
            },
          },
          {
            ui: 'custom',
            type: 'boolean',
            width: '140px',
            render: ({ setValue }) => {
              return (
                <EmailOtpButton
                  action="tx:withdraw"
                  disabled={!!insufficientNetworkFeeMessage}
                  onSuccess={() => {
                    setValue('send_otp_confirm', true);
                  }}
                />
              );
            },
            name: 'send_otp_confirm',
          },
        ],
      },
    ];
  }, [form.values, networks, tokens, balance, insufficientNetworkFeeMessage]);

  if (!token) {
    return null;
  }

  return (
    <div className="flex flex-col justify-start w-full h-full gap-2 py-3 px-5">
      <div className="flex flex-col gap-2 border-b border-solid border-border pb-4 mb-2">
        <div className="h-stack w-full gap-4 px-1 py-1.5 cursor-pointer">
          <TokenIcon token={token} width={40} height={40} />
          <div className="flex flex-col w-full">
            <Typography size="5" weight={500}>
              +{form.values.amount as string}
            </Typography>
            {formatted !== undefined && (
              <Typography size="2" color="#777777">
                ${formatted}
              </Typography>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <Typography size="1" weight={600}>
            To
          </Typography>
          <Typography size="1">{wallets.me[network]}</Typography>
        </div>
        {network !== 'solana' && (
          <div
            className={clsx('flex flex-col gap-1', {
              'text-destructive': insufficient,
            })}
          >
            <Typography size="1" weight={600}>
              Fee
            </Typography>
            <Typography size="1">
              {typeof withdraw_fee === 'string' && nativeToken
                ? `${web3.utils.formatUnits(
                  withdraw_fee,
                  nativeToken.decimals
                )} ${nativeToken.symbol}`
                : ''}
            </Typography>
          </div>
        )}
        {insufficientNetworkFeeMessage && (
          <div className="flex flex-col gap-1">
            <Typography size="1" className="text-destructive">
              {insufficientNetworkFeeMessage}
            </Typography>
          </div>
        )}
      </div>
      <Form
        {...form}
        name="withdraw"
        elements={elements}
        isLoading={isLoading || isThrottled}
        disabled={!!insufficientNetworkFeeMessage}
        submit={{
          label: t('common.withdraw'),
        }}
        onSubmit={withdraw}
      />
    </div>
  );
}
